import { ArrowRight, Check } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { cn } from '../../lib/utils';
import { configuredSections, type ServiceRequestType } from '../../lib/service-requests';

function Step({ number, title, detail, current, last }: { number: number; title: string; detail: string; current?: boolean; last?: boolean }) {
  return (
    <li className="relative flex gap-3 pb-5 last:pb-0" aria-current={current ? 'step' : undefined}>
      {last ? null : <span className="absolute left-[13px] top-7 h-[calc(100%-1.75rem)] w-px bg-border" aria-hidden />}
      <span
        className={cn(
          'flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold',
          current ? 'bg-primary text-primary-foreground' : 'border bg-background text-muted-foreground',
        )}
      >
        {number}
      </span>
      <span className="min-w-0 pt-0.5">
        <span className={cn('block text-sm font-semibold', !current && 'text-foreground/80')}>{title}</span>
        <span className="block text-xs text-muted-foreground">{detail}</span>
      </span>
    </li>
  );
}

/**
 * Shown straight after a draft is created: what happens next, taken from the type's own
 * configuration, so the requestor knows nothing starts until it is approved.
 */
export default function DraftCreatedDialog({
  requestKey,
  type,
  streamName,
  onFillIn,
  onLater,
}: {
  requestKey: string | null;
  type: ServiceRequestType | null | undefined;
  streamName: string | null;
  onFillIn: () => void;
  onLater: () => void;
}) {
  const sections = configuredSections(type).length;
  const handoff = type?.stage_config?.approved?.link_stream === true;

  return (
    <Dialog open={Boolean(requestKey)} onOpenChange={(open) => { if (!open) onLater(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader className="items-start text-left sm:text-left">
          <span className="mb-2 flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-600">
            <Check className="h-6 w-6" strokeWidth={2.5} aria-hidden />
          </span>
          <DialogTitle className="text-2xl">Draft saved</DialogTitle>
          <span className="flex items-center gap-2 text-sm text-muted-foreground">
            <span>{[requestKey, type?.name].filter(Boolean).join(' · ')}</span>
            <Badge variant="secondary" className="font-medium">Draft</Badge>
          </span>
          <DialogDescription className="pt-1">Complete the requirements to send your request for approval.</DialogDescription>
        </DialogHeader>
        <ol className="rounded-xl border bg-muted/30 p-4">
          <Step
            number={1}
            current
            title="Complete requirements"
            detail={`${sections ? `${sections} section${sections === 1 ? '' : 's'} · ` : ''}Your progress saves automatically`}
          />
          <Step number={2} title="Submit for approval" detail="Reviewed by the relevant departments" />
          <Step
            number={3}
            last
            title="Work begins"
            detail={handoff && streamName ? `Once approved, a task is created in ${streamName}` : 'Once approved, the delivery team takes it from here'}
          />
        </ol>
        <p className="text-xs text-muted-foreground">Work starts only after approval.</p>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={onLater}>Do this later</Button>
          <Button onClick={onFillIn}>
            Complete requirements
            <ArrowRight className="ml-1.5 h-4 w-4" aria-hidden />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
